import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Loader2 } from 'lucide-react';

export type UserRole = 'admin' | 'trader' | 'craftie' | 'craftsman';

interface PrivateRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requiredRole }) => {
  const location = useLocation();
  const { user: regularUser, loading: regularLoading } = useAuth();
  const { user: adminUser, loading: adminLoading } = useAdminAuth();

  const isLoading = requiredRole === 'admin' ? adminLoading : regularLoading;
  const currentUser = requiredRole === 'admin' ? adminUser : regularUser;

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
      </div>
    );
  }

  // If no user is authenticated, redirect to appropriate login page
  if (!currentUser) {
    const loginPath = requiredRole === 'admin' ? '/admin/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // If a specific role is required, check if user has that role
  if (requiredRole) {
    // For admin routes
    if (requiredRole === 'admin' && !adminUser) {
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }

    // For regular user routes with specific roles
    if (requiredRole !== 'admin') {
      if (!regularUser) {
        return <Navigate to="/login" state={{ from: location }} replace />;
      }
      
      const userRole = regularUser.user_metadata?.role;
      console.log('PrivateRoute - User role:', userRole, 'Required role:', requiredRole);
      
      if (requiredRole === 'craftsman') {
        if (userRole !== 'craftsman' && userRole !== 'craftie') {
          console.log('Access denied - role mismatch');
          return <Navigate to="/" replace />;
        }
      } else {
        if (userRole !== requiredRole) {
          console.log('Access denied - role mismatch');
          return <Navigate to="/" replace />;
        }
      }
    }
  }

  console.log('PrivateRoute - Access granted');
  return <>{children}</>;
};

export default PrivateRoute;
